import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { reportsAPI } from '../services/api';
import { SIMULATE_CHANGED_EVENT } from '../services/simulate';
import api from '../services/api';
import InfoIconButton from '../components/InfoIconButton';
import { useReportsHubNavigation } from '../contexts/ReportsHubNavigationContext';
import './ClientDelayListReport.css';

function csvEscape(val) {
  if (val === null || val === undefined) return '';
  const s = String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function formatDate(val) {
  if (val === null || val === undefined) return '';
  const s = String(val).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${Number(m[2])}/${Number(m[3])}/${m[1]}`;
  return s;
}

const COLUMNS = [
  { key: 'sc_name', label: 'SC', sortable: true, filter: true, narrow: true },
  { key: 'provider_name', label: 'Provider', sortable: true },
  { key: 'provider_type', label: 'Type', sortable: true },
  { key: 'client_name', label: 'Client', sortable: true },
  { key: 'delay_start_date', label: 'Delay Start', sortable: true },
  { key: 'delay_reason_description', label: 'Delay Reason', sortable: true },
  { key: 'delay_comment', label: 'Delay Comment', sortable: true },
  { key: 'city', label: 'City', sortable: true },
  { key: 'frequency', label: 'Frequency', sortable: true },
];

function compare(a, b, key) {
  const va = a[key];
  const vb = b[key];
  if (va == null && vb == null) return 0;
  if (va == null) return 1;
  if (vb == null) return -1;
  if (key === 'delay_start_date') {
    const da = new Date(va);
    const db = new Date(vb);
    return da.getTime() - db.getTime();
  }
  if (typeof va === 'number' && typeof vb === 'number') return va - vb;
  return String(va).localeCompare(String(vb), undefined, { sensitivity: 'base' });
}

const REPORT_DESCRIPTION = 'Open referrals with an active delay on the provider team (delay started, not ended) or a "Waiting" provider slot (e.g. Waiting DT/ST), even without a delay start date. Delays and Changes are listed in separate sections: a "Change" means the child already has that service (a second need for a discipline they already receive) — it is shown for tracking but is NOT reported to the state. Archived provider-team lines are excluded. Sort by clicking column headers; use the filter icon on Service Coordinator to narrow results.';

/**
 * Client Delay List report (legacy Team Tracking Filter option 5).
 * Lists open provider-team rows that either have an active delay (delay_start set, delay_end null)
 * or are a "Waiting" placeholder provider (e.g. Waiting DT/ST), even without a delay start date.
 */
export default function ClientDelayListReport({ embedded = false, refreshKey = 0, tenantCode = null }) {
  const reportsHubNav = useReportsHubNavigation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [roleCode, setRoleCode] = useState(null);
  const [staffDisplayName, setStaffDisplayName] = useState(null);
  const [tenantCodeFromWhoami, setTenantCodeFromWhoami] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);

  const [sortKey, setSortKey] = useState('sc_name');
  const [sortDir, setSortDir] = useState('asc');
  const [staffFilter, setStaffFilter] = useState(new Set());
  const [openFilterColumn, setOpenFilterColumn] = useState(null);
  const [draftStaffFilter, setDraftStaffFilter] = useState(new Set());
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0, width: 220 });

  const fetchWhoami = useCallback(async () => {
    try {
      const res = await api.get('/whoami');
      setRoleCode(res.data?.membership?.role_code || null);
      setStaffDisplayName(res.data?.staff_display_name ?? null);
      setTenantCodeFromWhoami(res.data?.tenant?.code ?? null);
    } catch {
      setRoleCode(null);
      setStaffDisplayName(null);
      setTenantCodeFromWhoami(null);
    }
  }, []);

  useEffect(() => {
    fetchWhoami();
  }, [fetchWhoami]);

  useEffect(() => {
    const handler = () => fetchWhoami();
    window.addEventListener(SIMULATE_CHANGED_EVENT, handler);
    return () => window.removeEventListener(SIMULATE_CHANGED_EVENT, handler);
  }, [fetchWhoami]);

  const runReport = useCallback(() => {
    setError(null);
    setLoading(true);
    reportsAPI
      .get('client-delay-list', {})
      .then((res) => {
        setRows(res.data?.rows || []);
        setError(null);
      })
      .catch((err) => {
        setRows([]);
        setError(err.response?.data?.error || 'Failed to load report.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    runReport();
  }, [runReport, refreshKey]);

  const handleSortClick = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const displayedRows = useMemo(() => {
    let out = rows;
    if (staffFilter.size > 0) {
      out = out.filter((r) => staffFilter.has(String(r.sc_name ?? '').trim() || '(blank)'));
    }
    out = [...out].sort((a, b) => {
      const cmp = compare(a, b, sortKey);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return out;
  }, [rows, staffFilter, sortKey, sortDir]);

  const distinctStaff = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => set.add(String(r.sc_name ?? '').trim() || '(blank)'));
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [rows]);

  useEffect(() => {
    if (!filterAnchor || !openFilterColumn) return;
    const r = filterAnchor.getBoundingClientRect();
    const padding = 12;
    let left = r.left;
    if (left + 220 > window.innerWidth - padding) left = window.innerWidth - 220 - padding;
    if (left < padding) left = padding;
    let top = r.bottom;
    const maxHeight = 320;
    if (top + maxHeight > window.innerHeight - padding) top = Math.max(padding, r.top - maxHeight);
    setPopoverPosition({ top, left, width: 220 });
  }, [filterAnchor, openFilterColumn]);

  const openFilter = (colKey, ev) => {
    ev.stopPropagation();
    setFilterAnchor(ev.currentTarget);
    setDraftStaffFilter(new Set(staffFilter));
    setOpenFilterColumn(colKey);
  };

  const applyFilter = () => {
    setStaffFilter(new Set(draftStaffFilter));
    setOpenFilterColumn(null);
    setFilterAnchor(null);
  };

  const clearFilter = () => {
    setStaffFilter(new Set());
    setDraftStaffFilter(new Set());
    setOpenFilterColumn(null);
    setFilterAnchor(null);
  };

  const toggleDraftStaff = (val) => {
    setDraftStaffFilter((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val);
      else next.add(val);
      return next;
    });
  };

  const downloadCsv = () => {
    const headers = ['type', ...COLUMNS.map((c) => c.key)];
    const lines = [
      headers.join(','),
      ...displayedRows.map((r) =>
        [
          csvEscape(r.issue_type === 'change' ? 'change' : 'delay'),
          ...COLUMNS.map((col) => {
            const v = r[col.key];
            if (col.key === 'delay_start_date') return csvEscape(formatDate(v));
            return csvEscape(v);
          }),
        ].join(',')
      ),
    ];
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'client-delay-list.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const titleSuffix = tenantCode || tenantCodeFromWhoami ? ` — ${tenantCode || tenantCodeFromWhoami}` : '';
  const hasResults = rows.length > 0;
  const isSc = roleCode === 'SERVICE_COORDINATOR';

  const activeFilterSummary = useMemo(() => {
    if (staffFilter.size === 0) return [];
    const names = [...staffFilter].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return [`Service Coordinator: ${names.join(', ')}`];
  }, [staffFilter]);

  const filterPopover =
    openFilterColumn &&
    createPortal(
      <div
        className="client-delay-list-filter-popover"
        style={{
          position: 'fixed',
          top: `${popoverPosition.top}px`,
          left: `${popoverPosition.left}px`,
          width: `${popoverPosition.width}px`,
        }}
        role="dialog"
        aria-label="Filter Service Coordinator"
      >
        <div className="client-delay-list-filter-popover-actions">
          <button type="button" className="btn btn-secondary" onClick={clearFilter}>Clear</button>
          <button type="button" className="btn btn-primary" onClick={applyFilter}>Apply</button>
        </div>
        <div className="client-delay-list-filter-popover-list">
          {distinctStaff.map((val) => (
            <label key={val} className="client-delay-list-filter-check">
              <input
                type="checkbox"
                checked={draftStaffFilter.has(val)}
                onChange={() => toggleDraftStaff(val)}
              />
              <span>{val}</span>
            </label>
          ))}
        </div>
        <div className="client-delay-list-filter-popover-actions">
          <button type="button" className="btn btn-secondary" onClick={clearFilter}>Clear</button>
          <button type="button" className="btn btn-primary" onClick={applyFilter}>Apply</button>
        </div>
      </div>,
      document.body
    );

  // A "change" (child already has this service) is shown separately from delays.
  const delayRows = displayedRows.filter((r) => r.issue_type !== 'change');
  const changeRows = displayedRows.filter((r) => r.issue_type === 'change');

  const renderTable = (sectionRows) => (
    <div className="client-delay-list-table-wrap">
      <table className="client-delay-list-table">
        <thead>
          <tr>
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                onClick={() => col.sortable && handleSortClick(col.key)}
                className={`${col.sortable ? 'client-delay-list-sortable' : ''} ${col.narrow ? 'client-delay-list-col-sc' : ''}`.trim()}
              >
                <div className="client-delay-list-header-cell">
                  <span className="client-delay-list-header-label">
                    {col.label}
                    {col.sortable && sortKey === col.key && (
                      <span className="client-delay-list-sort-arrow" aria-hidden="true">
                        {sortDir === 'asc' ? ' ↑' : ' ↓'}
                      </span>
                    )}
                  </span>
                  {col.filter && (
                    <span className="client-delay-list-header-filter-wrap" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className={`client-delay-list-filter-trigger ${staffFilter.size > 0 ? 'is-active' : ''}`}
                        onClick={(e) => openFilter(col.key, e)}
                        aria-label={`Filter ${col.label}`}
                      >
                        <svg className="client-delay-list-filter-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                          <path d="M4 6H20L14 13V18L10 20V13L4 6Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sectionRows.map((row, idx) => (
            <tr key={idx}>
              {COLUMNS.map((col) => {
                let cellVal = row[col.key];
                if (col.key === 'delay_start_date') cellVal = formatDate(cellVal) || '—';
                else if (cellVal == null || cellVal === '') cellVal = '—';
                return (
                  <td key={col.key} className={col.narrow ? 'client-delay-list-col-sc' : ''}>
                    {cellVal}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="client-delay-list-report">
      <div className="client-delay-list-title-row">
        <h2 className="client-delay-list-title">Client Delay List{titleSuffix}</h2>
        <InfoIconButton
          onClick={() => setInfoOpen((v) => !v)}
          ariaExpanded={infoOpen}
          ariaControls="client-delay-list-info"
          ariaLabel="Show report description"
          ariaLabelExpanded="Hide report description"
          className="client-delay-list-info-icon"
        />
      </div>
      {infoOpen && (
        <p id="client-delay-list-info" className="client-delay-list-info-text">
          {REPORT_DESCRIPTION}
        </p>
      )}
      {isSc && staffDisplayName && (
        <p className="client-delay-list-context" aria-live="polite">
          Service Coordinator: {staffDisplayName}
        </p>
      )}
      <div className="client-delay-list-actions">
        <button
          type="button"
          className="btn btn-outline"
          onClick={() => {
            if (reportsHubNav?.closeActiveReport) {
              reportsHubNav.closeActiveReport('Tracking');
              return;
            }
            setRows([]);
            setError(null);
            setStaffFilter(new Set());
          }}
        >
          Cancel
        </button>
        <button type="button" className="btn btn-primary client-delay-list-download-btn" onClick={downloadCsv} disabled={displayedRows.length === 0}>
          Export CSV
        </button>
        <button type="button" className="btn btn-primary client-delay-list-print-btn" onClick={() => window.print()} disabled={displayedRows.length === 0}>
          Print
        </button>
      </div>

      {activeFilterSummary.length > 0 && (
        <p className="client-delay-list-filter-summary no-print">
          Filters: {activeFilterSummary.join(' | ')}
        </p>
      )}

      {loading && rows.length === 0 && (
        <p className="client-delay-list-loading" aria-live="polite">Loading…</p>
      )}
      {error && (
        <div className="client-delay-list-error" role="alert">
          {error}
        </div>
      )}

      {hasResults && (
        <div className="client-delay-list-results">
          <h3 className="client-delay-list-section-title">Delays</h3>
          <p className="client-delay-list-count">
            {delayRows.length} row{delayRows.length !== 1 ? 's' : ''}
            {staffFilter.size > 0 && ' (filtered)'}.
          </p>
          {delayRows.length > 0
            ? renderTable(delayRows)
            : <p className="client-delay-list-empty">No delays.</p>}

          {changeRows.length > 0 && (
            <>
              <h3 className="client-delay-list-section-title client-delay-list-section-title-change">
                Changes <span className="client-delay-list-section-note">(not reported to the state)</span>
              </h3>
              <p className="client-delay-list-count">
                {changeRows.length} row{changeRows.length !== 1 ? 's' : ''}
                {staffFilter.size > 0 && ' (filtered)'}.
              </p>
              {renderTable(changeRows)}
            </>
          )}
        </div>
      )}
      {filterPopover}
    </div>
  );
}
